import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ApolloProvider } from '@apollo/client';
import { AutoDismissFlag } from '@atlaskit/flag';
import FlagGroup from '@atlaskit/flag/flag-group';
import { SpinnerMask } from '@atlassian/amkt-common';
import { deleteFormData, PARTNER_FORM_STATUS_TYPES, publishAppTrustData, saveAppTrustData, TRUST_DATA_FORM_STATES } from '@atlassian/mpac-api-client';
import { createApolloCache, createApolloClient } from '@atlassian/mpac-services-client/apollo-graphql-gateway';
import { FLAG_TYPE_ERROR, FLAG_TYPE_SUCCESS } from '@atlassian/mpac-types';
import { useAtlaskitFlags } from '@atlassian/mpac-ui';
import { DOMUtils, log } from '@atlassian/mpac-utils';
import React, { useEffect, useState } from 'react';
import { ANALYTICS_SAVE_AND_PREVIEW, ANALYTICS_SCREEN_PREVIEW_DRAFT_VIEW, ANALYTICS_SCREEN_PREVIEW_PUBLISH_VIEW, ANALYTICS_SCREEN_QUESTIONNAIRE_UI } from '../analytics';
import { useAppListingByIdForTrustTab, useAppTrustData } from '../hooks';
import { usePrivacySecurityAnalytics } from '../hooks/usePrivacySecurityAnalytics';
import { GetPrivacySecurityError } from '../utils';
import { BACKEND_VALIDATION_ERROR_ACTION_LINK, BACKEND_VALIDATION_ERROR_HEADING, BACKEND_VALIDATION_ERROR_MESSAGE, GENERIC_ERROR_HEADING, GENERIC_ERROR_MESSAGE, PARTNER_TRUST_APP_PREVIEW_MODES } from './constants';
import RenderByPriority from './utilities/RenderByPriority';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line max-lines-per-function
export const PartnerPrivacyAndSecurityContainer = _ref => {
  let {
    appKey,
    appId,
    appName,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    graphQLGatewayUrl
  } = _ref;
  const {
    flags,
    addFlag,
    dismissFlag
  } = useAtlaskitFlags();

  // this will only be set when an action is performed like "Save and Preview" / "Publish"
  const [currentMode, setCurrentMode] = useState();
  const {
    fireScreenEvent
  } = usePrivacySecurityAnalytics();
  useEffect(() => {
    if (currentMode === PARTNER_TRUST_APP_PREVIEW_MODES.PREVIEW_ONLY) {
      const showDraftLabel = (draftAppTrustData === null || draftAppTrustData === void 0 ? void 0 : draftAppTrustData.properties.formStatus) === PARTNER_FORM_STATUS_TYPES.SAVED;
      fireScreenEvent(ANALYTICS_SCREEN_PREVIEW_PUBLISH_VIEW, {
        draftSavedMessageVisible: showDraftLabel
      });
    }
    if (currentMode === PARTNER_TRUST_APP_PREVIEW_MODES.PREVIEW_AND_PUBLISH) {
      fireScreenEvent(ANALYTICS_SCREEN_PREVIEW_DRAFT_VIEW, {
        bannerType: 'PreviewInfoBanner'
      });
    }
    if (currentMode === PARTNER_TRUST_APP_PREVIEW_MODES.PARTNER_FORM) {
      fireScreenEvent(ANALYTICS_SCREEN_QUESTIONNAIRE_UI, {
        draftSavedMessageVisible: !!draftAppTrustData
      });
    }
  }, [currentMode]);

  // DRAFTED APP TRUST DATA
  const {
    appTrustData: draftAppTrustData,
    error: draftAppTrustError,
    isPending: draftAppTrustIsPending,
    reFetch: draftAppTrustRefetch
  } = useAppTrustData(mpacBaseUrl, appKey, TRUST_DATA_FORM_STATES.DRAFT);

  // LIVE APP TRUST DATA
  const {
    appTrustData: liveAppTrustData,
    error: liveAppTrustError,
    isPending: liveAppTrustIsPending,
    reFetch: liveAppTrustRefetch
  } = useAppTrustData(mpacBaseUrl, appKey, TRUST_DATA_FORM_STATES.LIVE);
  const {
    data: appListingData,
    error: appListingError,
    loading: appListingIsPending
  } = useAppListingByIdForTrustTab({
    appId,
    mpacBaseUrl,
    mpacCdnBaseUrl,
    showOnlyPublic: 'true'
  });
  const {
    fireButtonEvent
  } = usePrivacySecurityAnalytics();
  useEffect(() => {
    DOMUtils.scrollWindowToTop();
  }, [liveAppTrustData, draftAppTrustData, currentMode]);

  /**
   *
   * @returns Boolean: if the discard is successful then it will return true else false
   */
  const handleDiscard = async () => {
    try {
      // if there is no draft then donot call the API as it will throw the error
      // but there can be some local changes that need to revert so we have to follow rest of the flow
      if (draftAppTrustData) {
        await deleteFormData(mpacBaseUrl, appKey);
      }
    } catch (error) {
      //show error flag
      addFlag({
        id: Date.now().toString(),
        type: FLAG_TYPE_ERROR,
        title: 'Your work is safe, but there’s an issue',
        description: 'Please try again in some time.',
        isAutoDismiss: true
      });
      return false;
    }
    // refresh app trust data
    draftAppTrustRefetch();
    liveAppTrustRefetch();
    addFlag({
      id: Date.now().toString(),
      type: FLAG_TYPE_SUCCESS,
      title: 'Changes discarded successfully',
      description: 'Your responses have reverted back to the version which is currently live on the listing page.',
      isAutoDismiss: true
    });
    return true;
  };
  const errorComponent = GetPrivacySecurityError(appListingError, liveAppTrustError, draftAppTrustError, () => {
    draftAppTrustRefetch();
    liveAppTrustRefetch();
  });

  // Save form handler
  const handleSave = async formData => {
    try {
      await saveAppTrustData(mpacBaseUrl, appKey, formData);
    } catch (error) {
      if (error.status !== 200) {
        log.error('PartnerPrivacyAndSecurityContainer', "Failed to Save app trust data", {
          error,
          extraInfo: {
            appId
          }
        });
        let showValidationErrorMsg = false;

        // backend validation error
        if (error.status === 400) {
          var _errorParsed$errors, _errorParsed$errors$;
          const errorParsed = await error.error.json();
          fireButtonEvent(ANALYTICS_SAVE_AND_PREVIEW, {
            apiValidationErrors: errorParsed === null || errorParsed === void 0 ? void 0 : errorParsed.errors
          });
          showValidationErrorMsg = (errorParsed === null || errorParsed === void 0 ? void 0 : (_errorParsed$errors = errorParsed.errors) === null || _errorParsed$errors === void 0 ? void 0 : _errorParsed$errors[0]) &&
          // this is for the intermittent error 041
          ((_errorParsed$errors$ = errorParsed.errors[0].errorCode) === null || _errorParsed$errors$ === void 0 ? void 0 : _errorParsed$errors$.trim()) === '041'; // can simplify this in future to show all error messages as flags
        }
        if (showValidationErrorMsg) {
          addFlag({
            id: Date.now().toString(),
            type: FLAG_TYPE_ERROR,
            title: BACKEND_VALIDATION_ERROR_HEADING,
            description: BACKEND_VALIDATION_ERROR_MESSAGE,
            isAutoDismiss: true,
            actions: [{
              content: 'Learn more',
              target: '_blank',
              href: BACKEND_VALIDATION_ERROR_ACTION_LINK
            }]
          });
        } else {
          addFlag({
            id: Date.now().toString(),
            type: FLAG_TYPE_ERROR,
            title: GENERIC_ERROR_HEADING,
            description: GENERIC_ERROR_MESSAGE,
            isAutoDismiss: true
          });
        }
        return;
      }
    }
    fireButtonEvent(ANALYTICS_SAVE_AND_PREVIEW, {
      success: true
    });

    // refresh app trust data
    draftAppTrustRefetch();
    liveAppTrustRefetch();

    // show preview and publish only
    setCurrentMode(PARTNER_TRUST_APP_PREVIEW_MODES.PREVIEW_AND_PUBLISH);
    addFlag({
      id: Date.now().toString(),
      type: FLAG_TYPE_SUCCESS,
      title: 'Responses saved successfully',
      description: 'Your form is saved as a draft. After review, you can submit and publish to make the responses live. ',
      isAutoDismiss: true
    });
  };
  const handlePublish = async () => {
    try {
      await publishAppTrustData(mpacBaseUrl, appKey);
    } catch (error) {
      if (error.status !== 200) {
        log.error('PartnerPrivacyAndSecurityContainer', "Failed to Publish app trust data", {
          error,
          extraInfo: {
            appId
          }
        });

        //show error flag
        addFlag({
          id: Date.now().toString(),
          type: FLAG_TYPE_ERROR,
          title: 'Your work is safe, but there’s an issue',
          description: 'Please try again in some time.',
          isAutoDismiss: true
        });
        return;
      }
    }

    // refresh app trust data
    draftAppTrustRefetch();
    liveAppTrustRefetch();

    // show preview read only
    setCurrentMode(PARTNER_TRUST_APP_PREVIEW_MODES.PREVIEW_ONLY);

    //show success flag
    addFlag({
      id: Date.now().toString(),
      type: FLAG_TYPE_SUCCESS,
      title: 'Responses submitted successfully',
      description: "Responses submitted will go live immediately and trigger an internal review. We'll let you know when it gets accepted/rejected.",
      isAutoDismiss: true
    });
  };
  const handleEdit = () => {
    setCurrentMode(PARTNER_TRUST_APP_PREVIEW_MODES.PARTNER_FORM);
  };
  if (errorComponent) {
    return errorComponent;
  }

  // loading...
  if (draftAppTrustIsPending || appListingIsPending || liveAppTrustIsPending) {
    return /*#__PURE__*/_jsx(SpinnerMask, {
      active: true
    });
  }
  const apolloCache = createApolloCache();
  const version = process.env.RELEASE_VERSION || 'UNKNOWN';
  let apolloClient;
  if (graphQLGatewayUrl) {
    apolloClient = createApolloClient({
      uri: graphQLGatewayUrl,
      name: 'amkt',
      cache: apolloCache,
      version: "AMKT_".concat(version)
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(FlagGroup, {
      onDismissed: dismissFlag,
      children: flags.map(flag => /*#__PURE__*/_jsx(AutoDismissFlag, _objectSpread({}, flag), flag.id))
    }), !apolloClient && RenderByPriority({
      currentMode,
      liveAppTrustData,
      draftAppTrustData,
      appListingData,
      appName,
      onEdit: handleEdit,
      onPublish: handlePublish,
      onSave: handleSave,
      onDiscard: handleDiscard
    }), apolloClient && /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: RenderByPriority({
        currentMode,
        liveAppTrustData,
        draftAppTrustData,
        appListingData,
        appName,
        onEdit: handleEdit,
        onPublish: handlePublish,
        onSave: handleSave,
        onDiscard: handleDiscard
      })
    })]
  });
};
export default PartnerPrivacyAndSecurityContainer;